import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';

// @@CLIENTS_SORT_ZOHO_v101@@
// * Client list is sortable (default Name A–Z); the choice is remembered per browser.
// * "Zoho customers" panel (admin/manager): every cached Zoho Books customer, with
//   "In portal" (linked client) or an Activate button that creates + links a client.
// @@CLIENT_PROFILE_v102@@
// * Default sort is "Portal users first": bands with an active portal login on top
//   (then invite-pending, then the rest), A–Z inside each group, with section rows.
// * Portal column shows active logins / pending invites per band.

const STATUSES = ['prospect', 'active', 'paused', 'archived'];
const BLANK = {
  name: '', genre: '', hometown: '', shopify_url: '', drive_url: '',
  bandsintown_artist_id: '', notes: '', status: 'active',
};

// 0 = has an active portal login, 1 = invite pending only, 2 = no portal access
function portalRank(c) {
  if ((c.portal_active_users || 0) > 0) return 0;
  if ((c.portal_pending_users || 0) > 0) return 1;
  return 2;
}
const PORTAL_GROUPS = ['Active in the portal', 'Invite pending', 'Not in the portal'];

const SORTS = [
  { key: 'portal', label: 'Portal users first' },
  { key: 'name_asc', label: 'Name A–Z' },
  { key: 'name_desc', label: 'Name Z–A' },
  { key: 'created_desc', label: 'Newest added' },
  { key: 'created_asc', label: 'Oldest added' },
  { key: 'status', label: 'Status' },
];
const SORT_KEYS = SORTS.map((s) => s.key);
const SORT_STORE = 'hq.clients.sort.v102'; // new key so everyone starts on "Portal users first"
const ZOHO_OPEN_STORE = 'hq.clients.zohoOpen.v102'; // panel now below the list, collapsed by default

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

function readStore(k, fallback) {
  try { const v = window.localStorage.getItem(k); return v == null ? fallback : v; } catch (e) { return fallback; }
}
function writeStore(k, v) {
  try { window.localStorage.setItem(k, v); } catch (e) { /* private mode etc. */ }
}

// Blank names always sink to the bottom, whichever direction.
function byName(a, b) {
  const an = (a.name || '').trim();
  const bn = (b.name || '').trim();
  if (!an && bn) return 1;
  if (an && !bn) return -1;
  return collator.compare(an, bn);
}
function byCreated(a, b) {
  return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
}

export function sortClients(list, key) {
  const arr = [...list];
  switch (key) {
    case 'name_desc':
      return arr.sort((a, b) => {
        const an = (a.name || '').trim(); const bn = (b.name || '').trim();
        if (!an || !bn) return byName(a, b);
        return -collator.compare(an, bn);
      });
    case 'created_desc': return arr.sort((a, b) => byCreated(b, a) || byName(a, b));
    case 'created_asc': return arr.sort((a, b) => byCreated(a, b) || byName(a, b));
    case 'status':
      return arr.sort((a, b) => {
        const ai = STATUSES.indexOf(a.status); const bi = STATUSES.indexOf(b.status);
        return ((ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi)) || byName(a, b);
      });
    case 'portal':
      return arr.sort((a, b) => (portalRank(a) - portalRank(b)) || byName(a, b));
    case 'name_asc':
    default:
      return arr.sort(byName);
  }
}

export default function Clients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canActivate = !!user && (user.role === 'admin' || user.role === 'manager');

  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [msg, setMsg] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [sort, setSort] = useState(() => {
    const s = readStore(SORT_STORE, 'portal');
    return SORT_KEYS.includes(s) ? s : 'portal';
  });

  function changeSort(s) { setSort(s); writeStore(SORT_STORE, s); }

  async function load() {
    const res = await apiFetch('/api/clients');
    if (res.ok) { const d = await res.json(); setClients(d.clients || []); }
  }
  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => sortClients(clients, sort), [clients, sort]);

  async function create() {
    setMsg('');
    const res = await apiFetch('/api/clients', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    if (res.ok) { setForm(BLANK); setShowForm(false); load(); }
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not create client.'); }
  }

  const nameArrow = sort === 'name_asc' ? ' ↑' : sort === 'name_desc' ? ' ↓' : '';
  const thBtn = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

  const activeCount = clients.filter((c) => portalRank(c) === 0).length;

  function portalCell(c) {
    const a = c.portal_active_users || 0;
    const p = c.portal_pending_users || 0;
    if (!a && !p) return <span className="hq-v102-muted">—</span>;
    return (
      <span className="hq-v102-portalcell">
        {a > 0 && <span className="hq-pill hq-cstatus-active">{a} active</span>}
        {p > 0 && <span className="hq-pill hq-cstatus-paused">{p} invited</span>}
      </span>
    );
  }

  const rows = [];
  let lastGroup = null;
  sorted.forEach((c) => {
    if (sort === 'portal') {
      const g = portalRank(c);
      if (g !== lastGroup) {
        const n = sorted.filter((x) => portalRank(x) === g).length;
        rows.push(
          <tr key={'g' + g} className="hq-v102-grouprow">
            <td colSpan={5}>{PORTAL_GROUPS[g]} <span>{n}</span></td>
          </tr>
        );
        lastGroup = g;
      }
    }
    rows.push(
      <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/clients/${c.id}`)}>
        <td className="hq-v102-name">{c.name || '—'}</td>
        <td>{portalCell(c)}</td>
        <td>{c.genre || '—'}</td>
        <td>{c.hometown || '—'}</td>
        <td><span className={`hq-pill hq-cstatus-${c.status}`}>{c.status}</span></td>
      </tr>
    );
  });

  return (
    <div className="hq-page-content">
      <div className="hq-page-head hq-v102-head">
        <div>
          <h1>Clients</h1>
          <p>Bands &amp; companies you work with{clients.length ? ` · ${activeCount} active in the portal` : ''}.</p>
        </div>
        <button className="hq-btn" onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'New client'}
        </button>
      </div>

      {showForm && (
        <div className="hq-panel" style={{ marginBottom: 20 }}>
          <div className="hq-panel-head"><h2>New client</h2></div>
          <div style={{ padding: 20 }}>
            <div className="hq-form">
              <input className="hq-input" placeholder="Band / company name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ minWidth: 220 }} />
              <input className="hq-input" placeholder="Genre" value={form.genre}
                onChange={(e) => setForm({ ...form, genre: e.target.value })} />
              <input className="hq-input" placeholder="Hometown" value={form.hometown}
                onChange={(e) => setForm({ ...form, hometown: e.target.value })} />
              <input className="hq-input" placeholder="Shopify store URL" value={form.shopify_url}
                onChange={(e) => setForm({ ...form, shopify_url: e.target.value })} style={{ minWidth: 220 }} />
              <input className="hq-input" placeholder="Google Drive URL" value={form.drive_url}
                onChange={(e) => setForm({ ...form, drive_url: e.target.value })} style={{ minWidth: 220 }} />
              <input className="hq-input" placeholder="Bandsintown artist ID" value={form.bandsintown_artist_id}
                onChange={(e) => setForm({ ...form, bandsintown_artist_id: e.target.value })} />
              <select className="hq-select" value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input className="hq-input" placeholder="Notes" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ minWidth: 240 }} />
              <button className="hq-btn" onClick={create}>Save</button>
            </div>
            {msg && <p className="hq-msg">{msg}</p>}
          </div>
        </div>
      )}

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>All clients <span className="hq-v101-count">{clients.length}</span></h2>
          <label className="hq-v101-sortlabel">
            Sort
            <select className="hq-select hq-select-sm" value={sort} onChange={(e) => changeSort(e.target.value)}>
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="hq-table">
            <thead>
              <tr>
                <th style={thBtn} title="Sort by name"
                  onClick={() => changeSort(sort === 'name_asc' ? 'name_desc' : 'name_asc')}>Name{nameArrow}</th>
                <th style={thBtn} title="Portal users first"
                  onClick={() => changeSort('portal')}>Portal{sort === 'portal' ? ' ↑' : ''}</th>
                <th>Genre</th>
                <th>Hometown</th>
                <th style={thBtn} title="Sort by status"
                  onClick={() => changeSort('status')}>Status{sort === 'status' ? ' ↑' : ''}</th>
              </tr>
            </thead>
            <tbody>
              {rows}
              {clients.length === 0 && (
                <tr><td className="hq-empty" colSpan={5}>No clients yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {canActivate && <ZohoCustomersPanel onActivated={load} />}
    </div>
  );
}

// ───────────────────────── Zoho customers → activate ─────────────────────────
const FILTERS = [
  { key: 'not_in_portal', label: 'Not in portal' },
  { key: 'in_portal', label: 'In portal' },
  { key: 'all', label: 'All' },
];

function ZohoCustomersPanel({ onActivated }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => readStore(ZOHO_OPEN_STORE, '0') === '1');
  const [customers, setCustomers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState('not_in_portal');
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [busy, setBusy] = useState({});      // contactId -> true while a request runs
  const [matches, setMatches] = useState({}); // contactId -> { id, name, linked }
  const [rowErr, setRowErr] = useState({});   // contactId -> message
  const [msg, setMsg] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  function toggleOpen() { const n = !open; setOpen(n); writeStore(ZOHO_OPEN_STORE, n ? '1' : '0'); }

  async function load() {
    const res = await apiFetch('/api/clients/zoho-customers');
    if (res.ok) { const d = await res.json(); setCustomers(d.customers || []); }
    setLoaded(true);
  }
  useEffect(() => { load(); }, []);

  async function refresh() {
    setMsg(''); setRefreshing(true);
    try {
      const res = await apiFetch('/api/clients/zoho-customers/refresh', { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { await load(); setMsg(`Pulled ${d.synced} customers from Zoho.`); }
      else setMsg(d.error === 'zoho not configured' ? 'Zoho is not configured.'
        : d.error === 'could not reach zoho' ? 'Could not reach Zoho — try again.' : (d.error || 'Refresh failed.'));
    } finally { setRefreshing(false); }
  }

  function setRow(setter, id, v) {
    setter((m) => { const n = { ...m }; if (v === undefined) delete n[id]; else n[id] = v; return n; });
  }

  async function activate(zc, opts) {
    const id = zc.zoho_contact_id;
    setRow(setRowErr, id, undefined);
    setRow(setBusy, id, true);
    try {
      const res = await apiFetch(`/api/clients/zoho-customers/${encodeURIComponent(id)}/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(opts || {}),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setRow(setMatches, id, undefined);
        await load();
        if (onActivated) onActivated();
        return;
      }
      if (res.status === 409 && d.error === 'name_match' && d.match) { setRow(setMatches, id, d.match); return; }
      setRow(setRowErr, id, d.error || 'Could not activate.');
    } finally {
      setRow(setBusy, id, undefined);
    }
  }

  const inPortal = customers.filter((c) => c.client_id).length;
  const notInPortal = customers.length - inPortal;

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return customers.filter((c) => {
      if (filter === 'in_portal' && !c.client_id) return false;
      if (filter === 'not_in_portal' && c.client_id) return false;
      // Inactive-in-Zoho customers are hidden unless asked for — but linked ones always show under "In portal".
      if (!showInactive && c.status === 'inactive' && !(filter === 'in_portal' && c.client_id)) return false;
      if (!needle) return true;
      return [c.name, c.company_name, c.email, c.client_name]
        .some((v) => (v || '').toLowerCase().includes(needle));
    });
  }, [customers, filter, q, showInactive]);

  return (
    <div className="hq-panel" style={{ marginBottom: 20 }}>
      <div className="hq-panel-head">
        <h2>
          Zoho customers{' '}
          {loaded && customers.length > 0 && (
            <span className="hq-v101-sub">{customers.length} in Zoho · {inPortal} in portal · {notInPortal} not yet</span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {open && (
            <button className="hq-btn hq-btn-ghost" onClick={refresh} disabled={refreshing}>
              {refreshing ? 'Refreshing…' : 'Refresh from Zoho'}
            </button>
          )}
          <button className="hq-btn hq-btn-ghost" onClick={toggleOpen}>{open ? 'Hide' : 'Show'}</button>
        </div>
      </div>

      {open && (
        <>
          <div className="hq-v101-toolbar">
            <div className="hq-v101-seg" role="tablist">
              {FILTERS.map((f) => (
                <button key={f.key} role="tab" aria-selected={filter === f.key}
                  className={filter === f.key ? 'on' : ''} onClick={() => setFilter(f.key)}>
                  {f.label}
                </button>
              ))}
            </div>
            <input className="hq-input" placeholder="Search name, company, email" value={q}
              onChange={(e) => setQ(e.target.value)} style={{ minWidth: 220, flex: '1 1 220px', maxWidth: 360 }} />
            <label className="hq-v101-check">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Show inactive in Zoho
            </label>
          </div>
          {msg && <p className="hq-msg" style={{ margin: '0 20px 12px' }}>{msg}</p>}

          <div style={{ overflowX: 'auto' }}>
            <table className="hq-table">
              <thead>
                <tr><th>Name</th><th>Company</th><th>Email</th><th>Zoho</th><th style={{ textAlign: 'right' }}>Portal</th></tr>
              </thead>
              <tbody>
                {visible.map((c) => {
                  const id = c.zoho_contact_id;
                  const match = matches[id];
                  return (
                    <tr key={c.id}>
                      <td>{c.name || '—'}</td>
                      <td>{c.company_name || '—'}</td>
                      <td>{c.email || '—'}</td>
                      <td><span className="hq-v101-zstatus" data-s={c.status || ''}>{c.status || '—'}</span></td>
                      <td style={{ textAlign: 'right' }}>
                        {c.client_id ? (
                          <button className="hq-linkish" onClick={() => navigate(`/clients/${c.client_id}`)}>
                            In portal{c.client_name && c.client_name !== c.name ? ` · ${c.client_name}` : ''} →
                          </button>
                        ) : match ? (
                          <div className="hq-v101-match">
                            <span>“{match.name}” already exists{match.linked ? ' (linked to another Zoho customer)' : ''}.</span>
                            {!match.linked && (
                              <button className="hq-btn" disabled={!!busy[id]}
                                onClick={() => activate(c, { client_id: match.id })}>Link to it</button>
                            )}
                            <button className="hq-btn hq-btn-ghost" disabled={!!busy[id]}
                              onClick={() => activate(c, { force: true })}>Create new</button>
                            <button className="hq-linkish" onClick={() => setRow(setMatches, id, undefined)}>Cancel</button>
                          </div>
                        ) : (
                          <button className="hq-btn" disabled={!!busy[id]} onClick={() => activate(c)}>
                            {busy[id] ? 'Activating…' : 'Activate'}
                          </button>
                        )}
                        {rowErr[id] && <div className="hq-v101-err">{rowErr[id]}</div>}
                      </td>
                    </tr>
                  );
                })}
                {loaded && customers.length === 0 && (
                  <tr><td className="hq-empty" colSpan={5}>No Zoho customers cached yet — click “Refresh from Zoho”.</td></tr>
                )}
                {loaded && customers.length > 0 && visible.length === 0 && (
                  <tr><td className="hq-empty" colSpan={5}>
                    {filter === 'not_in_portal' && !q ? 'Every Zoho customer is already in the portal.' : 'No matches.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
